import React, { useEffect, useState } from "react";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

const OPERATOR = {
  platformName: "ZEDRO Academy",
  legalTradeName: "Académie ZEDRO",
  jurisdictionEs: "Québec, Canadá",
  jurisdictionFr: "Québec, Canada",
  website: "zedro.academy",
  supportEmail: "support@zedro.academy",
  adminEmail: "admin@zedro.academy",
  addressEs: "9165 Rue Lennon, Suite 412, Brossard, Québec, Canadá, J4Y 0R9",
  addressFr: "9165, rue Lennon, suite 412, Brossard, Québec, Canada, J4Y 0R9",
  phoneEs: "",
  phoneFr: "",
};

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

const TERMS_COPY = {
  es: {
    title: "Términos y Condiciones de Uso",
    lastUpdatedLabel: "Última actualización:",
    lastUpdated: "28 de junio de 2026",
    intro: [
      <>
        Bienvenido(a) a <strong>ZEDRO Academy</strong>, plataforma digital de
        formación práctica disponible en <strong>{OPERATOR.website}</strong> y
        operada bajo el nombre comercial registrado{" "}
        <strong>{OPERATOR.legalTradeName}</strong> en {OPERATOR.jurisdictionEs}.
      </>,
      <>
        Al acceder, crear una cuenta, iniciar una prueba gratuita, adquirir una
        membresía o utilizar la plataforma, aceptas estos Términos y
        Condiciones de Uso. Si no estás de acuerdo con ellos, no debes utilizar
        la plataforma.
      </>,
      <>
        Estos Términos aplican a la zona pública del sitio, la Store, el
        proceso de compra, la cuenta de usuario y el acceso al ecosistema
        formativo de ZEDRO Academy, salvo que una condición específica indique
        otra cosa o que la ley aplicable exija lo contrario.
      </>,
    ],
    sections: [
      {
        title: "1. Información del operador",
        paragraphs: [
          <>
            ZEDRO Academy es la plataforma digital y producto formativo operado
            bajo el nombre comercial registrado <strong>{OPERATOR.legalTradeName}</strong>{" "}
            en {OPERATOR.jurisdictionEs}.
          </>,
          <>
            Sitio web: <strong>{OPERATOR.website}</strong>
          </>,
          <>
            Correo de soporte: <a href={`mailto:${OPERATOR.supportEmail}`}>{OPERATOR.supportEmail}</a>
          </>,
          <>
            Correo administrativo/legal: <a href={`mailto:${OPERATOR.adminEmail}`}>{OPERATOR.adminEmail}</a>
          </>,
          <>
            Dirección comercial/postal: <strong>{OPERATOR.addressEs}</strong>
          </>,
          ],
      },
      {
        title: "2. Objeto de la plataforma",
        paragraphs: [
          <>
            ZEDRO Academy ofrece un ecosistema formativo digital con formación
            práctica progresiva, sistemas de avance, días estructurados,
            Bitácora ZEDRO, Conexión ZEDRO, micro-misiones y recursos prácticos
            orientados al orden interior, claridad, propósito, disciplina
            personal y aplicación diaria.
          </>,
          <>
            ZEDRO Academy no se presenta como una simple biblioteca de
            contenido, una aplicación devocional ni un curso aislado. La
            membresía da acceso a una experiencia formativa digital estructurada
            y progresiva.
          </>,
          <>
            El contenido de ZEDRO Academy es formativo, educativo e
            informativo. No constituye asesoría médica, psicológica, legal,
            financiera, terapéutica, contable, pastoral personalizada ni
            sustituto de acompañamiento profesional cuando este sea necesario.
          </>,
        ],
      },
      {
        title: "3. Idioma del contenido y de la información esencial",
        paragraphs: [
          <>
            ZEDRO Academy está disponible inicialmente en español. El contenido
            formativo principal del ecosistema se ofrece inicialmente en
            español.
          </>,
          <>
            La información legal, las condiciones de compra y los mensajes
            esenciales relacionados con pago, cancelación y cuenta pueden estar
            disponibles en español y francés. Cuando el usuario seleccione
            francés, la información pública y legal esencial se mostrará en
            francés en las páginas habilitadas.
          </>,
          <>
            Al iniciar una membresía, el usuario entiende que la experiencia
            formativa principal está disponible inicialmente en español, aunque
            la información legal y comercial esencial pueda presentarse también
            en francés.
          </>,
        ],
      },
      {
        title: "4. Acceso progresivo al contenido",
        paragraphs: [
          <>
            ZEDRO Academy está diseñada como una formación guiada y progresiva.
            Por esta razón, el acceso a sistemas, días, módulos, ejercicios,
            micro-misiones, recursos o contenidos puede activarse de forma
            gradual según la estructura definida por la plataforma.
          </>,
          <>
            La adquisición de una membresía no implica acceso inmediato a todos
            los sistemas, días, recursos o contenidos futuros de ZEDRO Academy.
            El contenido puede liberarse por etapas, periodos, días de avance,
            configuración interna o disponibilidad definida por la plataforma.
          </>,
          <>
            Esta estructura busca favorecer la aplicación práctica, la
            constancia y el proceso formativo. ZEDRO Academy no está diseñada
            como una biblioteca para consumo intensivo, descarga masiva o
            finalización acelerada de todos los contenidos en un corto periodo
            de tiempo.
          </>,
          <>
            Los horarios, ritmos de activación, secuencia de contenidos y
            disponibilidad de sistemas podrán ajustarse por razones pedagógicas,
            técnicas, operativas, de seguridad o de mejora de la experiencia,
            sin que ello implique incumplimiento del servicio cuando el acceso
            se mantenga conforme a la estructura ofrecida y a la ley aplicable.
          </>,
        ],
      },
      {
        title: "5. Registro y cuenta de usuario",
        paragraphs: [
          <>
            Para acceder a ciertas funciones, contenidos o membresías, deberás
            crear una cuenta con información veraz, actualizada y completa.
            Eres responsable de mantener la confidencialidad de tus credenciales
            de acceso y de toda actividad realizada desde tu cuenta.
          </>,
          <>
            El correo electrónico ingresado durante la compra o acceso puede
            utilizarse para verificar si ya existe una cuenta, una membresía
            activa o una suscripción que deba renovarse.
          </>,
          <>
            Si sospechas acceso no autorizado, pérdida de contraseña o uso
            indebido de tu cuenta, deberás comunicarlo a ZEDRO Academy lo antes
            posible a través de los canales de contacto indicados en estos
            Términos.
          </>,
        ],
      },
      {
        title: "6. Uso permitido",
        paragraphs: [
          <>
            Al usar ZEDRO Academy, aceptas utilizar la plataforma de manera
            lícita, respetuosa y coherente con su propósito formativo.
          </>,
        ],
        list: [
          "No intentar vulnerar, copiar, dañar o interferir la plataforma.",
          "No introducir malware, automatizaciones abusivas o código malicioso.",
          "No reproducir, vender, distribuir o explotar contenidos sin autorización escrita.",
          "No compartir tu cuenta con terceros ni permitir accesos no autorizados.",
          "No usar la metodología, materiales o contenidos con fines comerciales, institucionales o de formación externa sin permiso escrito.",
          "No publicar o almacenar contenido ilegal, abusivo, discriminatorio, ofensivo, engañoso o que vulnere derechos de terceros.",
        ],
      },
      {
        title: "7. Propiedad intelectual",
        paragraphs: [
          <>
            Todos los contenidos, textos, diseños, metodologías, estructura de
            sistemas, nombres de módulos, imágenes, recursos, interfaces,
            software, bases visuales, materiales formativos y elementos de marca
            disponibles en ZEDRO Academy pertenecen a ZEDRO Academy, a
            Académie ZEDRO o a sus respectivos licenciantes.
          </>,
          <>
            La compra de una membresía o el acceso a la plataforma no transfiere
            derechos de propiedad intelectual. Se concede únicamente una
            licencia limitada, personal, revocable, no exclusiva y no
            transferible para acceder y utilizar los contenidos dentro de la
            plataforma durante el periodo de acceso correspondiente.
          </>,
        ],
      },
      {
        title: "8. Contenido creado por el usuario",
        paragraphs: [
          <>
            Algunas funciones de ZEDRO Academy permiten escribir, registrar,
            guardar o generar contenido personal, como respuestas, reflexiones,
            bitácora, evidencias, micro-misiones, peticiones, decisiones,
            registros de avance y preferencias de lectura.
          </>,
          <>
            El contenido que ingreses sigue siendo tuyo. Sin embargo, otorgas a
            ZEDRO Academy una licencia limitada para alojar, almacenar,
            procesar, mostrar y utilizar ese contenido dentro de tu cuenta con
            el fin de operar la plataforma, guardar tu progreso, personalizar tu
            experiencia, prestar soporte, mantener seguridad técnica y cumplir
            obligaciones legales.
          </>,
          <>
            Eres responsable del contenido que ingresas en la plataforma. No
            debes introducir información que vulnere derechos de terceros ni
            contenido ilegal, ofensivo, dañino o contrario al uso permitido.
          </>,
        ],
      },
      {
        title: "9. Privacidad y datos personales",
        paragraphs: [
          <>
            ZEDRO Academy puede recopilar y almacenar información necesaria para
            operar la plataforma, incluyendo información de cuenta, correo
            electrónico, estado de membresía, progreso, respuestas, registros de
            bitácora, preferencias, actividad dentro de la plataforma, datos
            técnicos y comunicaciones de soporte.
          </>,
          <>
            La información ingresada por los usuarios puede quedar almacenada en
            bases de datos y servicios tecnológicos utilizados por ZEDRO Academy
            para prestar el servicio. El tratamiento de la información personal
            se explica con mayor detalle en la Política de Privacidad.
          </>,
          <>
            ZEDRO Academy implementa medidas razonables de seguridad para
            proteger la información. Sin embargo, ningún sistema digital puede
            garantizar seguridad, disponibilidad o recuperación absoluta frente
            a fallas técnicas, errores humanos, pérdida de acceso, ataques,
            incidentes de seguridad o eventos fuera de control razonable.
          </>,
        ],
      },
      {
        title: "10. Funciones basadas en IA",
        paragraphs: [
          <>
            Algunas funciones actuales o futuras de ZEDRO Academy pueden
            utilizar herramientas de inteligencia artificial para apoyar la
            organización, reflexión, búsqueda, escritura o acompañamiento
            general del usuario.
          </>,
          <>
            Las respuestas generadas con apoyo de IA pueden contener errores,
            imprecisiones o interpretaciones incompletas. El usuario debe usar
            su criterio y no debe considerar estos resultados como asesoría
            profesional, médica, psicológica, legal, financiera o espiritual
            personalizada.
          </>,
          <>
            Recomendamos no ingresar información altamente sensible o innecesaria
            en funciones asistidas por IA.
          </>,
        ],
      },
      {
        title: "11. Servicios de pago, precio, moneda e impuestos",
        paragraphs: [
          <>
            ZEDRO Academy puede ofrecer productos digitales, membresías,
            suscripciones, contenidos formativos y otros servicios pagos. Los
            precios, moneda, impuestos aplicables, periodo de prueba, frecuencia
            de cobro, restricciones y condiciones principales serán informados
            antes de confirmar la compra.
          </>,
          <>
            El precio de la membresía mensual será el precio vigente mostrado en
            la Store y/o en Stripe antes de completar la suscripción. Si el
            precio se muestra en dólares canadienses, el pago se realizará
            en CAD, salvo que se indique expresamente otra moneda antes de
            confirmar el pago.
          </>,
          <>
            Los impuestos aplicables, si corresponden, se calcularán y mostrarán
            antes de confirmar la suscripción. Dependiendo del país, banco,
            emisor de tarjeta o método de pago del usuario, pueden aplicarse
            tasas de conversión, comisiones bancarias u otros cargos externos
            que no son controlados por ZEDRO Academy.
          </>,
          <>
            Los pagos son procesados por proveedores externos de pago, como
            Stripe. ZEDRO Academy no almacena números completos de tarjeta de
            crédito o débito.
          </>,
        ],
      },
      {
        title: "12. Prueba gratuita, renovación y cobro automático",
        paragraphs: [
          <>
            Algunas membresías pueden incluir una prueba gratuita de 7 días con
            tarjeta registrada. Durante la prueba, no se realizará ningún cobro
            y el usuario tendrá acceso a los contenidos incluidos en la
            membresía conforme a las condiciones vigentes para el periodo de
            prueba.
          </>,
          <>
            Si el usuario cancela antes de finalizar los 7 días gratuitos, no
            se realizará el primer cobro mensual.
          </>,
          <>
            Si el usuario no cancela antes de finalizar la prueba gratuita, la
            membresía mensual se activará automáticamente y se cobrará el precio
            vigente mostrado antes de confirmar el pago, más los impuestos
            aplicables si corresponden.
          </>,
          <>
            Después del primer cobro, la membresía se renovará mensualmente
            hasta que el usuario la cancele o hasta que ZEDRO Academy suspenda o
            finalice el servicio conforme a estos Términos y la ley aplicable.
          </>,
        ],
      },
      {
        title: "13. Cancelación y reembolsos",
        paragraphs: [
          <>
            Puedes cancelar tu membresía cuando quieras. La cancelación evita
            futuras renovaciones y futuros cobros, pero no elimina
            automáticamente el acceso ya pagado.
          </>,
          <>
            Si cancelas durante la prueba gratuita antes de su finalización, no
            se realizará el primer cobro mensual.
          </>,
          <>
            Si cancelas después de un cobro mensual, conservarás el acceso hasta
            el final del periodo ya pagado, salvo que la ley aplicable exija o
            reconozca un derecho distinto.
          </>,
          <>
            Los pagos ya realizados no son reembolsables por cancelación
            anticipada del mes en curso y no se realizan reembolsos
            proporcionales por días no utilizados, salvo que la ley aplicable
            exija lo contrario.
          </>,
          <>
            En caso de error técnico, cobro duplicado, fraude comprobado o
            situación que deba revisarse conforme a la ley aplicable, ZEDRO
            Academy evaluará el caso y realizará los ajustes correspondientes
            cuando proceda.
          </>,
        ],
      },
      {
        title: "14. Disputas de pago y contracargos",
        paragraphs: [
          <>
            Los pagos realizados mediante tarjeta u otros métodos procesados por
            proveedores externos pueden estar sujetos a procesos de disputa,
            contracargo o revisión por parte del emisor del método de pago, las
            redes de pago o el proveedor correspondiente.
          </>,
          <>
            Si se inicia una disputa o contracargo, ZEDRO Academy podrá
            presentar evidencia del acceso otorgado, aceptación de términos, uso
            de la plataforma, historial de facturación y comunicaciones
            relacionadas, de acuerdo con las reglas del proveedor de pago y la
            ley aplicable.
          </>,
        ],
      },
      {
        title: "15. Disponibilidad, cambios y mejoras",
        paragraphs: [
          <>
            ZEDRO Academy busca mantener la plataforma disponible y funcional,
            pero no garantiza disponibilidad continua, ausencia total de
            errores, compatibilidad perfecta con todos los dispositivos o
            interrupciones cero.
          </>,
          <>
            Podemos realizar mantenimiento, mejoras, cambios de diseño,
            actualización de contenidos, ajustes técnicos o modificaciones de
            funcionalidades cuando sea necesario para mejorar, proteger o
            sostener la plataforma.
          </>,
          <>
            Cuando un cambio afecte de manera material el precio, obligaciones
            principales del usuario o elementos esenciales de la membresía,
            ZEDRO Academy procurará notificarlo con anticipación razonable y
            respetará los derechos que exija la ley aplicable.
          </>,
        ],
      },
      {
        title: "16. Resultados personales",
        paragraphs: [
          <>
            ZEDRO Academy ofrece formación, estructura y herramientas prácticas,
            pero no garantiza resultados específicos. Los avances personales
            dependen de múltiples factores, incluyendo la constancia del
            usuario, su contexto, decisiones, aplicación práctica y
            circunstancias personales.
          </>,
          <>
            Ningún contenido debe interpretarse como promesa de transformación
            automática, resultado espiritual, emocional, económico, profesional,
            relacional, físico o personal garantizado.
          </>,
        ],
      },
      {
        title: "17. Suspensión o terminación de acceso",
        paragraphs: [
          <>
            ZEDRO Academy podrá suspender, limitar o cerrar una cuenta cuando
            exista incumplimiento de estos Términos, riesgo de seguridad, uso
            indebido, fraude, abuso, disputa de pago, requerimiento legal o
            conducta que afecte la integridad de la plataforma o de otros
            usuarios.
          </>,
          <>
            Cuando sea razonable, se podrá notificar al usuario. Sin embargo, en
            casos de riesgo, abuso, fraude o seguridad, la suspensión podrá
            realizarse sin aviso previo, en la medida permitida por la ley
            aplicable.
          </>,
        ],
      },
      {
        title: "18. Enlaces y servicios de terceros",
        paragraphs: [
          <>
            La plataforma puede integrar o enlazar servicios externos, como
            procesadores de pago, proveedores de correo, almacenamiento,
            analítica, alojamiento, herramientas de comunicación o recursos
            complementarios. El uso de dichos servicios puede estar sujeto a sus
            propios términos y políticas.
          </>,
          <>
            ZEDRO Academy no controla completamente las prácticas,
            disponibilidad o políticas de terceros, aunque busca trabajar con
            proveedores confiables y razonables para la operación de la
            plataforma.
          </>,
        ],
      },
      {
        title: "19. Limitación de responsabilidad",
        paragraphs: [
          <>
            En la máxima medida permitida por la ley aplicable, ZEDRO Academy,
            Académie ZEDRO, sus colaboradores, proveedores y representantes no
            serán responsables por daños indirectos, incidentales, especiales,
            pérdida de datos, pérdida de oportunidad, interrupciones, fallas
            técnicas, pérdida de acceso, decisiones tomadas por el usuario o
            resultados derivados del uso o imposibilidad de uso de la
            plataforma.
          </>,
          <>
            Nada en estos Términos limita derechos que no puedan ser limitados o
            renunciados bajo la ley aplicable de protección al consumidor u
            otras normas obligatorias.
          </>,
        ],
      },
      {
        title: "20. Modificaciones de estos Términos",
        paragraphs: [
          <>
            Podemos actualizar estos Términos para reflejar cambios legales,
            técnicos, comerciales o de funcionamiento de la plataforma. La
            versión vigente será la publicada en el sitio web.
          </>,
          <>
            Si los cambios son relevantes, ZEDRO Academy podrá comunicarlo por
            medios razonables, como aviso dentro de la plataforma, correo
            electrónico o actualización visible en el sitio.
          </>,
          <>
            Los cambios no afectarán retroactivamente periodos ya pagados,
            salvo que la ley aplicable permita o exija otra cosa.
          </>,
        ],
      },
      {
        title: "21. Legislación aplicable",
        paragraphs: [
          <>
            Estos Términos se interpretan conforme a las leyes aplicables en la
            provincia de Québec y las leyes federales de Canadá que
            correspondan, sin perjuicio de los derechos obligatorios que puedan
            proteger al consumidor según su lugar de residencia.
          </>,
          <>
            Si alguna disposición de estos Términos se considera inválida o
            inaplicable, las demás disposiciones conservarán su validez en la
            máxima medida permitida por la ley.
          </>,
        ],
      },
      {
        title: "22. Contacto",
        paragraphs: [
          <>
            Para consultas sobre estos Términos, membresías, pagos, cancelación
            o uso de la plataforma, puedes escribir a:
          </>,
          <>
            📧 <a href={`mailto:${OPERATOR.supportEmail}`}>{OPERATOR.supportEmail}</a>
          </>,
        ],
      },
    ],
  },
  fr: {
    title: "Conditions générales d’utilisation",
    lastUpdatedLabel: "Dernière mise à jour :",
    lastUpdated: "28 juin 2026",
    intro: [
      <>
        Bienvenue sur <strong>ZEDRO Academy</strong>, une plateforme numérique
        de formation pratique disponible sur <strong>{OPERATOR.website}</strong>{" "}
        et exploitée sous le nom commercial enregistré{" "}
        <strong>{OPERATOR.legalTradeName}</strong> au {OPERATOR.jurisdictionFr}.
      </>,
      <>
        En accédant à la plateforme, en créant un compte, en commençant un essai
        gratuit, en souscrivant un abonnement ou en utilisant la plateforme,
        vous acceptez les présentes Conditions générales d’utilisation. Si vous
        n’êtes pas d’accord avec celles-ci, vous ne devez pas utiliser la
        plateforme.
      </>,
      <>
        Ces Conditions s’appliquent à la zone publique du site, à la Boutique,
        au processus d’achat, au compte utilisateur et à l’accès à l’écosystème
        formatif de ZEDRO Academy, sauf indication contraire ou exigence
        contraire de la loi applicable.
      </>,
    ],
    sections: [
      {
        title: "1. Informations sur l’exploitant",
        paragraphs: [
          <>
            ZEDRO Academy est la plateforme numérique et le produit formatif
            exploité sous le nom commercial enregistré{" "}
            <strong>{OPERATOR.legalTradeName}</strong> au {OPERATOR.jurisdictionFr}.
          </>,
          <>
            Site Web : <strong>{OPERATOR.website}</strong>
          </>,
          <>
            Courriel d’assistance : <a href={`mailto:${OPERATOR.supportEmail}`}>{OPERATOR.supportEmail}</a>
          </>,
          <>
            Courriel administratif/juridique : <a href={`mailto:${OPERATOR.adminEmail}`}>{OPERATOR.adminEmail}</a>
          </>,
          <>
            Adresse commerciale/postale : <strong>{OPERATOR.addressFr}</strong>
          </>,
        ],
      },
      {
        title: "2. Objet de la plateforme",
        paragraphs: [
          <>
            ZEDRO Academy offre un écosystème formatif numérique comprenant une
            formation pratique progressive, des systèmes de progression, des
            journées structurées, Bitácora ZEDRO, Conexión ZEDRO, des
            micro-missions et des ressources pratiques orientées vers l’ordre
            intérieur, la clarté, le sens, la discipline personnelle et
            l’application quotidienne.
          </>,
          <>
            ZEDRO Academy ne se présente pas comme une simple bibliothèque de
            contenu, une application dévotionnelle ni un cours isolé.
            L’abonnement donne accès à une expérience formative numérique
            structurée et progressive.
          </>,
          <>
            Le contenu de ZEDRO Academy est formatif, éducatif et informatif. Il
            ne constitue pas un avis médical, psychologique, juridique,
            financier, thérapeutique, comptable, pastoral personnalisé ni un
            substitut à un accompagnement professionnel lorsque celui-ci est
            nécessaire.
          </>,
        ],
      },
      {
        title: "3. Langue du contenu et des informations essentielles",
        paragraphs: [
          <>
            ZEDRO Academy est actuellement disponible principalement en
            espagnol. Le contenu formatif principal de l’écosystème est offert
            initialement en espagnol.
          </>,
          <>
            Les informations légales, les conditions d’achat et les messages
            essentiels liés au paiement, à l’annulation et au compte peuvent
            être disponibles en espagnol et en français. Lorsque l’utilisateur
            sélectionne le français, les informations publiques et légales
            essentielles seront affichées en français sur les pages activées.
          </>,
          <>
            En commençant un abonnement, l’utilisateur comprend que l’expérience
            formative principale est initialement disponible en espagnol, même
            si les informations légales et commerciales essentielles peuvent
            également être présentées en français.
          </>,
        ],
      },
      {
        title: "4. Accès progressif au contenu",
        paragraphs: [
          <>
            ZEDRO Academy est conçue comme une formation guidée et progressive.
            Pour cette raison, l’accès aux systèmes, journées, modules,
            exercices, micro-missions, ressources ou contenus peut être activé
            graduellement selon la structure définie par la plateforme.
          </>,
          <>
            L’achat d’un abonnement n’implique pas un accès immédiat à tous les
            systèmes, journées, ressources ou contenus futurs de ZEDRO Academy.
            Le contenu peut être libéré par étapes, périodes, journées de
            progression, configuration interne ou disponibilité définie par la
            plateforme.
          </>,
          <>
            Cette structure vise à favoriser l’application pratique, la
            constance et le processus formatif. ZEDRO Academy n’est pas conçue
            comme une bibliothèque destinée à la consommation intensive, au
            téléchargement massif ou à l’achèvement accéléré de tous les
            contenus en peu de temps.
          </>,
          <>
            Les horaires, rythmes d’activation, séquences de contenu et
            disponibilités des systèmes peuvent être ajustés pour des raisons
            pédagogiques, techniques, opérationnelles, de sécurité ou
            d’amélioration de l’expérience, sans que cela constitue un manquement
            au service lorsque l’accès demeure conforme à la structure offerte
            et à la loi applicable.
          </>,
        ],
      },
      {
        title: "5. Inscription et compte utilisateur",
        paragraphs: [
          <>
            Pour accéder à certaines fonctions, contenus ou abonnements, vous
            devez créer un compte avec des informations exactes, à jour et
            complètes. Vous êtes responsable de la confidentialité de vos
            identifiants et de toute activité effectuée depuis votre compte.
          </>,
          <>
            L’adresse courriel saisie lors de l’achat ou de l’accès peut être
            utilisée pour vérifier s’il existe déjà un compte, un abonnement
            actif ou une souscription à renouveler.
          </>,
          <>
            Si vous soupçonnez un accès non autorisé, une perte de mot de passe
            ou une utilisation abusive de votre compte, vous devez communiquer
            avec ZEDRO Academy dès que possible au moyen des coordonnées
            indiquées dans les présentes Conditions.
          </>,
        ],
      },
      {
        title: "6. Utilisation permise",
        paragraphs: [
          <>
            En utilisant ZEDRO Academy, vous acceptez d’utiliser la plateforme
            de manière licite, respectueuse et conforme à son objectif formatif.
          </>,
        ],
        list: [
          "Ne pas tenter de compromettre, copier, endommager ou perturber la plateforme.",
          "Ne pas introduire de logiciels malveillants, d’automatisations abusives ou de code malveillant.",
          "Ne pas reproduire, vendre, distribuer ou exploiter les contenus sans autorisation écrite.",
          "Ne pas partager votre compte avec des tiers ni permettre des accès non autorisés.",
          "Ne pas utiliser la méthodologie, les documents ou les contenus à des fins commerciales, institutionnelles ou de formation externe sans autorisation écrite.",
          "Ne pas publier ni stocker de contenu illégal, abusif, discriminatoire, offensant, trompeur ou portant atteinte aux droits de tiers.",
        ],
      },
      {
        title: "7. Propriété intellectuelle",
        paragraphs: [
          <>
            Tous les contenus, textes, designs, méthodologies, structures de
            systèmes, noms de modules, images, ressources, interfaces,
            logiciels, bases visuelles, matériels formatifs et éléments de
            marque disponibles sur ZEDRO Academy appartiennent à ZEDRO Academy,
            à Académie ZEDRO ou à leurs concédants respectifs.
          </>,
          <>
            L’achat d’un abonnement ou l’accès à la plateforme ne transfère
            aucun droit de propriété intellectuelle. Il est accordé uniquement
            une licence limitée, personnelle, révocable, non exclusive et non
            transférable pour accéder aux contenus et les utiliser dans la
            plateforme pendant la période d’accès correspondante.
          </>,
        ],
      },
      {
        title: "8. Contenu créé par l’utilisateur",
        paragraphs: [
          <>
            Certaines fonctions de ZEDRO Academy permettent d’écrire,
            d’enregistrer, de conserver ou de générer du contenu personnel,
            comme des réponses, réflexions, journaux, évidences, micro-missions,
            demandes, décisions, registres de progression et préférences de
            lecture.
          </>,
          <>
            Le contenu que vous saisissez demeure le vôtre. Toutefois, vous
            accordez à ZEDRO Academy une licence limitée pour héberger,
            stocker, traiter, afficher et utiliser ce contenu dans votre compte
            afin d’exploiter la plateforme, conserver votre progression,
            personnaliser votre expérience, fournir l’assistance, maintenir la
            sécurité technique et respecter les obligations légales.
          </>,
          <>
            Vous êtes responsable du contenu que vous saisissez dans la
            plateforme. Vous ne devez pas introduire d’information portant
            atteinte aux droits de tiers ni de contenu illégal, offensant,
            nuisible ou contraire à l’utilisation permise.
          </>,
        ],
      },
      {
        title: "9. Confidentialité et données personnelles",
        paragraphs: [
          <>
            ZEDRO Academy peut recueillir et conserver les informations
            nécessaires à l’exploitation de la plateforme, notamment les
            informations de compte, l’adresse courriel, l’état de l’abonnement,
            la progression, les réponses, les registres de journal, les
            préférences, l’activité dans la plateforme, les données techniques et
            les communications d’assistance.
          </>,
          <>
            Les informations saisies par les utilisateurs peuvent être stockées
            dans des bases de données et services technologiques utilisés par
            ZEDRO Academy pour fournir le service. Le traitement des
            renseignements personnels est expliqué plus en détail dans la
            Politique de confidentialité.
          </>,
          <>
            ZEDRO Academy met en place des mesures raisonnables de sécurité pour
            protéger l’information. Toutefois, aucun système numérique ne peut
            garantir une sécurité, disponibilité ou récupération absolue face à
            des défaillances techniques, erreurs humaines, pertes d’accès,
            attaques, incidents de sécurité ou événements hors de contrôle
            raisonnable.
          </>,
        ],
      },
      {
        title: "10. Fonctions basées sur l’IA",
        paragraphs: [
          <>
            Certaines fonctions actuelles ou futures de ZEDRO Academy peuvent
            utiliser des outils d’intelligence artificielle pour soutenir
            l’organisation, la réflexion, la recherche, l’écriture ou
            l’accompagnement général de l’utilisateur.
          </>,
          <>
            Les réponses générées avec l’appui de l’IA peuvent contenir des
            erreurs, imprécisions ou interprétations incomplètes. L’utilisateur
            doit exercer son jugement et ne doit pas considérer ces résultats
            comme un avis professionnel, médical, psychologique, juridique,
            financier ou spirituel personnalisé.
          </>,
          <>
            Nous recommandons de ne pas saisir d’informations hautement
            sensibles ou non nécessaires dans les fonctions assistées par l’IA.
          </>,
        ],
      },
      {
        title: "11. Services payants, prix, devise et taxes",
        paragraphs: [
          <>
            ZEDRO Academy peut offrir des produits numériques, abonnements,
            souscriptions, contenus formatifs et autres services payants. Les
            prix, la devise, les taxes applicables, la période d’essai, la
            fréquence de paiement, les restrictions et les conditions
            principales seront communiqués avant la confirmation de l’achat.
          </>,
          <>
            Le prix de l’abonnement mensuel sera le prix en vigueur affiché dans
            la Boutique et/ou dans Stripe avant de compléter l’abonnement. Si le
            prix est affiché en dollars canadiens, le paiement sera effectué en
            CAD, sauf indication expresse d’une autre devise avant la
            confirmation du paiement.
          </>,
          <>
            Les taxes applicables, le cas échéant, seront calculées et affichées
            avant la confirmation de l’abonnement. Selon le pays, la banque,
            l’émetteur de la carte ou le mode de paiement de l’utilisateur, des
            frais de conversion, commissions bancaires ou autres frais externes
            non contrôlés par ZEDRO Academy peuvent s’appliquer.
          </>,
          <>
            Les paiements sont traités par des fournisseurs externes de paiement,
            comme Stripe. ZEDRO Academy ne conserve pas les numéros complets de
            carte de crédit ou de débit.
          </>,
        ],
      },
      {
        title: "12. Essai gratuit, renouvellement et paiement automatique",
        paragraphs: [
          <>
            Certains abonnements peuvent inclure un essai gratuit de 7 jours
            avec une carte enregistrée. Pendant l’essai, aucun paiement ne sera
            effectué et l’utilisateur aura accès aux contenus inclus dans
            l’abonnement conformément aux conditions applicables à la période
            d’essai.
          </>,
          <>
            Si l’utilisateur annule avant la fin des 7 jours gratuits, le
            premier paiement mensuel ne sera pas effectué.
          </>,
          <>
            Si l’utilisateur n’annule pas avant la fin de l’essai gratuit,
            l’abonnement mensuel sera activé automatiquement et le prix en
            vigueur affiché avant la confirmation du paiement sera facturé, plus
            les taxes applicables le cas échéant.
          </>,
          <>
            Après le premier paiement, l’abonnement sera renouvelé mensuellement
            jusqu’à ce que l’utilisateur l’annule ou jusqu’à ce que ZEDRO
            Academy suspende ou mette fin au service conformément aux présentes
            Conditions et à la loi applicable.
          </>,
        ],
      },
      {
        title: "13. Annulation et remboursements",
        paragraphs: [
          <>
            Vous pouvez annuler votre abonnement à tout moment. L’annulation
            évite les renouvellements et paiements futurs, mais ne supprime pas
            automatiquement l’accès déjà payé.
          </>,
          <>
            Si vous annulez pendant l’essai gratuit avant sa fin, le premier
            paiement mensuel ne sera pas effectué.
          </>,
          <>
            Si vous annulez après un paiement mensuel, vous conserverez l’accès
            jusqu’à la fin de la période déjà payée, sauf si la loi applicable
            exige ou reconnaît un droit différent.
          </>,
          <>
            Les paiements déjà effectués ne sont pas remboursables en cas
            d’annulation anticipée du mois en cours et aucun remboursement
            proportionnel n’est effectué pour les jours non utilisés, sauf si la
            loi applicable l’exige autrement.
          </>,
          <>
            En cas d’erreur technique, de paiement en double, de fraude avérée
            ou de situation devant être examinée conformément à la loi
            applicable, ZEDRO Academy évaluera le cas et effectuera les
            ajustements appropriés lorsque cela sera requis.
          </>,
        ],
      },
      {
        title: "14. Contestations de paiement et rétrofacturations",
        paragraphs: [
          <>
            Les paiements effectués par carte ou par d’autres méthodes traitées
            par des fournisseurs externes peuvent être soumis à des processus de
            contestation, de rétrofacturation ou de révision par l’émetteur du
            mode de paiement, les réseaux de paiement ou le fournisseur
            concerné.
          </>,
          <>
            En cas de contestation ou de rétrofacturation, ZEDRO Academy pourra
            présenter des preuves de l’accès accordé, de l’acceptation des
            conditions, de l’utilisation de la plateforme, de l’historique de
            facturation et des communications liées, conformément aux règles du
            fournisseur de paiement et à la loi applicable.
          </>,
        ],
      },
      {
        title: "15. Disponibilité, changements et améliorations",
        paragraphs: [
          <>
            ZEDRO Academy cherche à maintenir la plateforme disponible et
            fonctionnelle, mais ne garantit pas une disponibilité continue,
            l’absence totale d’erreurs, une compatibilité parfaite avec tous les
            appareils ni l’absence totale d’interruptions.
          </>,
          <>
            Nous pouvons effectuer des maintenances, améliorations, changements
            de design, mises à jour de contenus, ajustements techniques ou
            modifications de fonctionnalités lorsque cela est nécessaire pour
            améliorer, protéger ou soutenir la plateforme.
          </>,
          <>
            Lorsqu’un changement affecte matériellement le prix, les obligations
            principales de l’utilisateur ou les éléments essentiels de
            l’abonnement, ZEDRO Academy s’efforcera de le notifier avec un
            préavis raisonnable et respectera les droits exigés par la loi
            applicable.
          </>,
        ],
      },
      {
        title: "16. Résultats personnels",
        paragraphs: [
          <>
            ZEDRO Academy offre une formation, une structure et des outils
            pratiques, mais ne garantit pas de résultats spécifiques. Les
            progrès personnels dépendent de multiples facteurs, notamment la
            constance de l’utilisateur, son contexte, ses décisions, son
            application pratique et ses circonstances personnelles.
          </>,
          <>
            Aucun contenu ne doit être interprété comme une promesse de
            transformation automatique ou de résultat spirituel, émotionnel,
            économique, professionnel, relationnel, physique ou personnel
            garanti.
          </>,
        ],
      },
      {
        title: "17. Suspension ou fin de l’accès",
        paragraphs: [
          <>
            ZEDRO Academy peut suspendre, limiter ou fermer un compte en cas de
            non-respect des présentes Conditions, de risque de sécurité,
            d’utilisation abusive, de fraude, d’abus, de contestation de
            paiement, d’exigence légale ou de comportement affectant l’intégrité
            de la plateforme ou d’autres utilisateurs.
          </>,
          <>
            Lorsque cela est raisonnable, l’utilisateur pourra être avisé.
            Toutefois, en cas de risque, d’abus, de fraude ou de sécurité, la
            suspension pourra être effectuée sans préavis, dans la mesure
            permise par la loi applicable.
          </>,
        ],
      },
      {
        title: "18. Liens et services de tiers",
        paragraphs: [
          <>
            La plateforme peut intégrer ou lier des services externes, comme des
            processeurs de paiement, fournisseurs de courriel, stockage,
            analytique, hébergement, outils de communication ou ressources
            complémentaires. L’utilisation de ces services peut être soumise à
            leurs propres conditions et politiques.
          </>,
          <>
            ZEDRO Academy ne contrôle pas entièrement les pratiques,
            disponibilités ou politiques de tiers, bien qu’elle cherche à
            travailler avec des fournisseurs fiables et raisonnables pour
            l’exploitation de la plateforme.
          </>,
        ],
      },
      {
        title: "19. Limitation de responsabilité",
        paragraphs: [
          <>
            Dans toute la mesure permise par la loi applicable, ZEDRO Academy,
            Académie ZEDRO, ses collaborateurs, fournisseurs et représentants ne
            seront pas responsables des dommages indirects, accessoires,
            spéciaux, pertes de données, pertes d’occasion, interruptions,
            défaillances techniques, pertes d’accès, décisions prises par
            l’utilisateur ou résultats découlant de l’utilisation ou de
            l’impossibilité d’utiliser la plateforme.
          </>,
          <>
            Rien dans les présentes Conditions ne limite les droits qui ne
            peuvent être limités ou auxquels il ne peut être renoncé en vertu de
            la législation applicable en matière de protection du consommateur
            ou d’autres règles obligatoires.
          </>,
        ],
      },
      {
        title: "20. Modifications des présentes Conditions",
        paragraphs: [
          <>
            Nous pouvons mettre à jour les présentes Conditions afin de refléter
            des changements juridiques, techniques, commerciaux ou de
            fonctionnement de la plateforme. La version en vigueur sera celle
            publiée sur le site Web.
          </>,
          <>
            Si les changements sont importants, ZEDRO Academy pourra les
            communiquer par des moyens raisonnables, comme un avis dans la
            plateforme, un courriel ou une mise à jour visible sur le site.
          </>,
          <>
            Les changements n’affecteront pas rétroactivement les périodes déjà
            payées, sauf si la loi applicable le permet ou l’exige.
          </>,
        ],
      },
      {
        title: "21. Loi applicable",
        paragraphs: [
          <>
            Les présentes Conditions sont interprétées conformément aux lois
            applicables dans la province de Québec et aux lois fédérales du
            Canada qui s’appliquent, sans préjudice des droits obligatoires qui
            peuvent protéger le consommateur selon son lieu de résidence.
          </>,
          <>
            Si une disposition des présentes Conditions est jugée invalide ou
            inapplicable, les autres dispositions conserveront leur validité dans
            toute la mesure permise par la loi.
          </>,
        ],
      },
      {
        title: "22. Contact",
        paragraphs: [
          <>
            Pour toute question concernant les présentes Conditions, les
            abonnements, les paiements, l’annulation ou l’utilisation de la
            plateforme, vous pouvez écrire à :
          </>,
          <>
            📧 <a href={`mailto:${OPERATOR.supportEmail}`}>{OPERATOR.supportEmail}</a>
          </>,
        ],
      },
    ],
  },
};

export default function TermsConditions() {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function syncLanguage(nextLanguage) {
      if (["es", "fr"].includes(nextLanguage)) {
        setLanguage(nextLanguage);
      } else {
        setLanguage("es");
      }
    }

    function handleLanguageEvent(event) {
      syncLanguage(event?.detail?.language);
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event.newValue);
      }
    }

    syncLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    window.addEventListener("zedro:languagechange", handleLanguageEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("zedro:languagechange", handleLanguageEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const copy = TERMS_COPY[language] || TERMS_COPY.es;

  return (
    <div
      style={{
        maxWidth: 900,
        margin: "2rem auto",
        padding: "0 1rem",
        fontFamily: "Questrial, sans-serif",
        lineHeight: 1.75,
        color: "#2c3b51",
      }}
    >
      <h1>{copy.title}</h1>

      <p>
        <strong>{copy.lastUpdatedLabel}</strong> {copy.lastUpdated}
      </p>

      {copy.intro.map((paragraph, index) => (
        <p key={`intro-${index}`}>{paragraph}</p>
      ))}

      {copy.sections.map((section) => (
        <section key={section.title}>
          <h2 style={{ color: "#286b94" }}>{section.title}</h2>

          {section.paragraphs?.map((paragraph, index) => (
            <p key={`${section.title}-p-${index}`}>{paragraph}</p>
          ))}

          {Array.isArray(section.list) && (
            <ul>
              {section.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}
